// src/data/productEducation.js
// VERSION: ACME Execution V5 (Passport Model Integrated)

export const productEducationData = {
  meta: {
    title: "ACME Sales Academy",
    subtitle:
      "Pivoting from generic 'one-size-fits-all' training to a Role-Based, Precision-Guided Framework.",
    endState:
      "End State: A field-ready sales force measured by 'Solvency' (Certification) and Impact (Revenue), not just completion.",
    outcomes: [
      "Role-Specific Competency (The 3x4 Matrix)",
      "Standardized 'Stand & Deliver' Certification",
      "Just-in-Time Battle Cards for 3 Product Lines",
    ],
  },

  strategy: {
    heading: "The 3x4 Matrix Strategy",

    // 1. STRATEGIC ANCHORS
    northStar: {
      label: "The Core Shift",
      statement:
        "From 'Generic Knowledge Transfer' to 'Role-Based Performance Support' (The 5 Moments of Need).",
    },

    // 2. TABS CONTENT
    tabs: {
      executive: {
        body: "We are moving away from a flat library to a matrixed curriculum. The strategy sits at the intersection of ACME's 3 Product Lines (Vendor, Buyer, Watcher) and our 4 Learner Roles. We will prioritize 'Phase 1: Universal Core' for all, followed by specialized tracks for AEs and Specialists.",
        proofSignals: {
          leading: [
            "Certification Pass Rate",
            "Pitch Video Submissions",
            "Search Volume",
          ],
          lagging: [
            "Time-to-First-Deal",
            "Win Rates (by Product Line)",
            "Cross-Sell Ratio",
          ],
        },
      },

      // MAPPING "SAGES" TO "LEARNER PERSONAS"
      sages: {
        intro: "The 4 Learner Personas (Who we are building for)",
        sages: [
          {
            name: "The Account Executive (AE)",
            philosophy: "The Quarterback",
            bullets: [
              "Focus: Value Props & Pricing",
              "Needs: 'Why Buy?' & 'Who Buys?'",
              "Key Asset: Challenger Discovery Deck",
            ],
            appliesTo: ["Sales", "Closing"],
            showsUpIn: ["Phase 2", "Certification"],
          },
          {
            name: "Product Specialist (PS)",
            philosophy: "The SME",
            bullets: [
              "Focus: Technical Methodology",
              "Needs: 'How it Works' & Data Granularity",
              "Key Asset: Technical Whiteboard Script",
            ],
            appliesTo: ["Pre-Sales", "Demo"],
            showsUpIn: ["Phase 2", "Deep Dives"],
          },
          {
            name: "Customer Success (CSM)",
            philosophy: "The Farmer",
            bullets: [
              "Focus: Usage & Adoption",
              "Needs: Platform Training & Renewal Defense",
              "Key Asset: QBR Template",
            ],
            appliesTo: ["Post-Sales", "Renewal"],
            showsUpIn: ["Phase 2", "Optimization"],
          },
          {
            name: "Vertical Specialist (VSS)",
            philosophy: "The Industry Expert",
            bullets: [
              "Focus: Sector Trends",
              "Needs: Regulations & Use Cases",
              "Key Asset: Industry Battle Card",
            ],
            appliesTo: ["Strategic", "Verticals"],
            showsUpIn: ["Phase 2", "Advanced"],
          },
        ],
      },

      // DELIVERABLES LISTS
      deliverables: {
        core: [
          "12x Matrix Decks (3 Products x 4 Roles)",
          "Competitor Battle Cards (vs. Gartner/Forrester)",
          "Persona Discovery Decks (CMO/CIO/PM)",
          "The 'Data Journey' Whiteboard Visual",
        ],
        cert: [
          "The 'Stand & Deliver' Pitch Rubric",
          "NPI Video Pitch Requirements (60 seconds)",
          "Manager Scorecards in Salesforce",
        ],
        operatingRhythms: [
          "Bi-Weekly Stakeholder Review",
          "Monthly 'Launch Sprint' for NPI",
          "Quarterly Curriculum Refresh",
        ],
      },
    },

    // 3. THE MATRIX GRID DATA (Asset Templates)
    matrix: {
      columns: ["IT Vendor (GTM)", "IT Buyer (CIO)", "IT Watcher (Investor)"],
      rows: [
        {
          role: "Account Exec (AE)",
          focus: "Value & Pricing",
          assets: [
            {
              type: "Deck",
              title: "Commercial Value Prop",
              status: "ready",
              templateType: "Deck",
              sampleContent: {
                subtitle:
                  "Unlocking Revenue Growth with ACME Market Intelligence",
              },
            },
            {
              type: "Deck",
              title: "ROI & Benchmarking",
              status: "building",
              templateType: "Deck",
              sampleContent: {
                subtitle: "Proving Business Impact: A Data-Driven Approach",
              },
            },
            {
              type: "One-Pager",
              title: "Alpha Generation Pitch",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "The Edge",
                    body: "Leverage ACME's proprietary datasets to identify pre-market signals.",
                  },
                  {
                    heading: "Key Metrics",
                    body: "Gain visibility into valuation models and M&A targets before they hit the news.",
                  },
                ],
              },
            },
          ],
        },
        {
          role: "Product Spec (PS)",
          focus: "Tech & Data",
          assets: [
            {
              type: "Whiteboard",
              title: "Data Ingestion Flow",
              status: "pending",
              templateType: "Whiteboard",
              sampleContent: {
                sketches:
                  "Results found from\n\n(Sketch of a flow chart)\n[Raw Data Sources] -> (API Connector) -> [ACME Ingestion Engine] -> (Normalization & Cleaning) -> [Master Data Lake] -> (Query Layer) -> [Client Dashboard/Feed]\n\nKey Definitions:\n- API Connector: Secure, real-time link.\n- Normalization: Mapping disparate formats to a unified schema.",
              },
            },
            {
              type: "Demo",
              title: "API Integration",
              status: "building",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Overview",
                    body: "Standard REST API protocols available for enterprise clients.",
                  },
                ],
              },
            },
            {
              type: "Spec Sheet",
              title: "Valuation Models",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Methodology",
                    body: "Our models use a 3-stage validation process to ensure accuracy.",
                  },
                ],
              },
            },
          ],
        },
        {
          role: "Customer Success (CSM)",
          focus: "Adoption",
          assets: [
            {
              type: "Playbook",
              title: "Marketing Use Cases",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Target Audience",
                    body: "CMOs and Marketing Directors looking for ROI attribution.",
                  },
                  {
                    heading: "Discovery Questions",
                    body: "1. How are you currently measuring campaign effectiveness?\n2. What data gaps exist in your current attribution models?",
                  },
                  {
                    heading: "The ACME Solution",
                    body: "Position our 'Attribution Connector' to link ad spend directly to downstream revenue.",
                  },
                ],
              },
            },
            {
              type: "Guide",
              title: "Contract Renewal",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Timeline",
                    body: "Start the renewal process 90 days before contract expiration.",
                  },
                ],
              },
            },
            {
              type: "Checklist",
              title: "Data Feed Health",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Daily Checks",
                    body: "- [ ] Verify API connection status\n- [ ] Check error logs for anomalies",
                  },
                ],
              },
            },
          ],
        },
        {
          role: "Vertical Spec (VSS)",
          focus: "Industry Context",
          assets: [
            {
              type: "Battle Card",
              title: "Retail Tech Trends",
              status: "building",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Market Shift",
                    body: "Move from omnichannel to 'unified commerce'.",
                  },
                  {
                    heading: "ACME's Play",
                    body: "Our supply chain data provides visibility others lack.",
                  },
                ],
              },
            },
            {
              type: "Battle Card",
              title: "Healthcare IT Spend",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Key Trend",
                    body: "Increased investment in telehealth infrastructure.",
                  },
                ],
              },
            },
            {
              type: "Battle Card",
              title: "FinTech M&A Data",
              status: "pending",
              templateType: "Document",
              sampleContent: {
                sections: [
                  {
                    heading: "Overview",
                    body: "Consolidation in the payment processing sector is driving demand for diligence data.",
                  },
                ],
              },
            },
          ],
        },
      ],
    },

    // 4. ROLE JOURNEYS (Updated for Passport Model)
    roleJourneys: {
      AE: {
        role: "Account Executive",
        theme: "The Challenger",
        goal: "Command the commercial conversation.",
        phases: [
          {
            name: "Phase 1: The Core (Days 1–30)",
            items: [
              "Module: History of ACME (The Gold Standard)",
              "Module: The Three Pillars (Vendor/Buyer/Watcher)",
              "Methodology 101: Forecasting & Counting",
              "Tools: SFDC & Sales Enablement Portal"
            ],
          },
          {
            name: "Phase 2: Specialization (Days 31–60)",
            items: [
              "Focus: 'Why Buy?' & 'Who Buys?'",
              "Pitch: MarketScape vs. Competitors",
              "Pitch: Sizing the TAM (Trackers)",
              "Battle Card: Gartner Trap-Setting",
            ],
          },
          {
            name: "Phase 3: Certification (Days 61–90)",
            items: [
              "Shadowing: 3x Live Calls",
              "NPI Launch: 60-Second Video Pitch",
              "Capstone: 'Stand & Deliver' Mock Pitch",
            ],
          },
        ],
      },
      PS: {
        role: "Product Specialist",
        theme: "The Architect",
        goal: "Translate complexity into capability.",
        phases: [
          {
            name: "Phase 1: The Core (Days 1–30)",
            items: [
              "Module: History of ACME (The Gold Standard)",
              "Module: The Three Pillars (Vendor/Buyer/Watcher)",
              "Data Taxonomy 101",
              "Tools: Technical Scoping Configuration"
            ],
          },
          {
            name: "Phase 2: Specialization (Days 31–60)",
            items: [
              "Focus: 'How it Works' & 'Data Granularity'",
              "Deep Dive: Scoring Methodology",
              "Demo: API Integration & Data Cuts",
              "Tech Sheet: Security & Compliance",
            ],
          },
          {
            name: "Phase 3: Certification (Days 61–90)",
            items: [
              "Whiteboarding: Data Ingestion Flow",
              "Build: Custom Pivot Table Sandbox",
              "Capstone: Technical Q&A Panel",
            ],
          },
        ],
      },
      CSM: {
        role: "Customer Success",
        theme: "The Trusted Advisor",
        goal: "Drive adoption and defend the renewal.",
        phases: [
          {
            name: "Phase 1: The Core (Days 1–30)",
            items: [
              "Module: History of ACME (The Gold Standard)",
              "Module: The Three Pillars (Vendor/Buyer/Watcher)",
              "Support Ticket Workflows",
              "Usage Gap Analysis Training",
            ],
          },
          {
            name: "Phase 2: Specialization (Days 31–60)",
            items: [
              "Workflow: Extracting Graphics for Decks",
              "Setup: Auto-Alerts for Data Updates",
              "Playbook: Marketing ROI Use Cases",
              "Guide: Contract Renewal Timeline",
            ],
          },
          {
            name: "Phase 3: Certification (Days 61–90)",
            items: [
              "Simulation: The 'At-Risk' Call",
              "Deliverable: QBR Presentation Deck",
              "Capstone: Renewal Defense Scenario",
            ],
          },
        ],
      },
    },
  },

  timeline: {
    heading: "Execution Roadmap",
    phases: [
      {
        phaseName: "Phase 1: Universal Core",
        duration: "Days 1–30",
        purpose: "Establish the baseline. History, Methodology, and Tools.",
        deliverablesHeading: "Completed Assets",
        deliverables: [
          "History of ACME Module",
          "Methodology 101 (Forecasting)",
          "SFDC & Portal Training",
          "Universal Discovery Questions",
        ],
      },
      {
        phaseName: "Phase 2: Alignment (The Build)",
        duration: "Days 31–60",
        purpose: "Constructing the 3x4 Matrix. Building role-specific paths.",
        deliverablesHeading: "Current Sprints",
        deliverables: [
          "Build 'IT Vendor' Track (AE/PS/CSM)",
          "Build 'IT Buyer' Track (AE/PS/CSM)",
          "Develop Battle Cards & Cheat Sheets",
          "Map Phase 2 Content Roadmap",
        ],
        subSections: [
          {
            title: "Critical Priority",
            bullets: [
              "Finalize the 'IT Vendor' Deck first—it drives 60% of revenue.",
              "Interview Product VP for 'Watcher' product context.",
            ],
          },
        ],
      },
      {
        phaseName: "Phase 3: Launch & Measure",
        duration: "Days 61–90",
        purpose: "Go-live with SharePoint site and Certification rigor.",
        deliverablesHeading: "Upcoming Deliverables",
        deliverables: [
          "Launch SharePoint 'Passport' Site",
          "Rollout 'Stand & Deliver' Certification",
          "Activate Commission Code Logic in SFDC",
        ],
      },
    ],
  },

  resources: {
    heading: "Resourcing",
    minimumViableTeam: {
      title: "Current Squad",
      items: [
        "Product Education Lead (You)",
        "1x SME per Product Line",
        "Sales Ops (Data Access)",
      ],
    },
    idealTeam: {
      title: "Future Scale",
      items: ["Instructional Designer", "Video Editor", "LMS Admin"],
    },
  },

  risks: {
    heading: "Risks",
    rows: [
      {
        risk: "SME Availability",
        mitigation:
          "Pre-book interview blocks; use existing recording transcripts.",
      },
      {
        risk: "Low Adoption",
        mitigation:
          "Tie Certification to Commission Code unlock (Force Rank).",
      },
      { risk: "Content Decay", mitigation: "Quarterly 'Vitality' audits." },
    ],
  },
};
